import { useMemo } from "react";
import { formatProductName } from "../helpers";

export interface BreakdownItem {
  name: string;
  estimated_cost: number;
  pct_of_total: number;
  total_dbus: number;
}

export interface ChartDataItem {
  name: string;
  value: number;
  pct: number;
}

interface UseChartDataOptions {
  breakdown: BreakdownItem[];
  topCount?: number;
  otherLabel?: (count: number) => string;
}

export const useChartData = ({
  breakdown,
  topCount = 8,
  otherLabel = count => `Other (${count})`,
}: UseChartDataOptions) => {
  return useMemo(() => {
    // Table rows: pass through server order so user-controlled sort survives.
    const tableBreakdown = breakdown.filter(item => item.estimated_cost > 0);

    // Chart top-N: always cost-desc regardless of table sort.
    const byCostDesc = [...tableBreakdown].sort((a, b) => b.estimated_cost - a.estimated_cost);
    const topItems = byCostDesc.slice(0, topCount);
    const otherItems = byCostDesc.slice(topCount);
    const otherTotal = otherItems.reduce((sum, item) => sum + item.estimated_cost, 0);
    const otherPct = otherItems.reduce((sum, item) => sum + item.pct_of_total, 0);

    const chartData: ChartDataItem[] = [
      ...topItems.map(item => ({
        name: formatProductName(item.name),
        value: item.estimated_cost,
        pct: item.pct_of_total,
      })),
      ...(otherItems.length > 0
        ? [
            {
              name: otherLabel(otherItems.length),
              value: otherTotal,
              pct: otherPct,
            },
          ]
        : []),
    ];

    return { chartData, sortedBreakdown: tableBreakdown };
  }, [breakdown, topCount, otherLabel]);
};
